/**
 * useBulkChangeStageSubmit — shared submit logic for the bulk change-stage
 * endpoint, used by both BulkChangeStageModal ("Change Stage") and
 * BulkMoveToPipelineModal ("Move to pipeline").
 *
 * Encapsulates: building the selection discriminator (entity_ids / filters /
 * all), the sync `has_more` chunk loop, async dispatch, and toast/error
 * handling. The only difference between the two callers is the
 * `allowPipelineChange` flag and the success/incomplete copy, both passed per
 * call to `submit`.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type {
  AssociationTypeName,
  BulkActionSelection,
} from '@/lib/crm/core/CRMObjectClient';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { translateBulkChangeStageError } from '@/lib/crm/core/errors';
import type { CrmObject, EntityTypeName, FilterGroup } from '@/types/crm';

const MAX_SYNC_ITERATIONS = 20;

export interface BulkChangeStageSubmitMessages {
  /** Toast title on full sync success. */
  successTitle: string;
  /** Toast description on full sync success. */
  successDescription: string;
  /** Toast error shown when the sync loop hits its iteration cap (partial). */
  incomplete: string;
}

export interface BulkChangeStageSubmitArgs {
  targetStageId: string;
  /** When true, the server allows moving across pipelines ("Move to pipeline"). */
  allowPipelineChange?: boolean;
  messages: BulkChangeStageSubmitMessages;
}

export interface UseBulkChangeStageSubmitParams {
  orgId: number;
  entityTypeName: EntityTypeName;
  associationTypeName: AssociationTypeName;
  selectedObjects: CrmObject[];
  isSelectAllMode: boolean;
  filterGroups?: FilterGroup[];
  excludedEntityIds?: string[];
  search?: string;
  searchProperties?: string[];
  onAsyncDispatched: (jobId: number, totalCount: number) => void;
  onSyncComplete: () => void;
  onClose: () => void;
}

export interface UseBulkChangeStageSubmitResult {
  submit: (args: BulkChangeStageSubmitArgs) => Promise<void>;
  isSubmitting: boolean;
}

export function useBulkChangeStageSubmit({
  orgId,
  entityTypeName,
  associationTypeName,
  selectedObjects,
  isSelectAllMode,
  filterGroups,
  excludedEntityIds,
  search,
  searchProperties,
  onAsyncDispatched,
  onSyncComplete,
  onClose,
}: UseBulkChangeStageSubmitParams): UseBulkChangeStageSubmitResult {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Stable selection input — pick the discriminator that matches intent:
  //   - explicit row selection → entity_ids
  //   - select-all + positive selector (filterGroups or search) → filters
  //   - select-all with no selector → all (explicit opt-in to unscoped)
  const selection = useMemo<BulkActionSelection>(() => {
    if (!isSelectAllMode) {
      return {
        kind: 'entity_ids',
        entityIds: selectedObjects.map(o => o.entity_id),
      };
    }
    const hasFilters = !!filterGroups && filterGroups.length > 0;
    const hasSearch = search != null && search !== '';
    if (hasFilters || hasSearch) {
      return {
        kind: 'filters',
        filterGroups,
        excludedEntityIds,
        search,
        searchProperties,
      };
    }
    return { kind: 'all', excludedEntityIds };
  }, [
    isSelectAllMode,
    filterGroups,
    excludedEntityIds,
    search,
    searchProperties,
    selectedObjects,
  ]);

  const submit = useCallback(
    async ({
      targetStageId,
      allowPipelineChange,
      messages,
    }: BulkChangeStageSubmitArgs) => {
      if (!targetStageId || isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      const client = createCRMObjectClient(orgId, entityTypeName);

      try {
        const firstResponse = await client.bulkChangeStage(
          entityTypeName,
          associationTypeName,
          targetStageId,
          selection,
          allowPipelineChange
        );

        if (firstResponse.mode === 'async') {
          onAsyncDispatched(firstResponse.job_id, firstResponse.total_count);
          onClose();
          return;
        }

        // Only the select-all / filter-based path can produce `has_more` — the
        // explicit entity_ids path always processes the full set in a single
        // chunk and returns has_more=false.
        let hasMore = firstResponse.has_more;
        let iterations = 1;
        let hitIterationCap = false;

        while (hasMore) {
          iterations += 1;
          if (iterations > MAX_SYNC_ITERATIONS) {
            hitIterationCap = true;
            break;
          }
          const response = await client.bulkChangeStage(
            entityTypeName,
            associationTypeName,
            targetStageId,
            selection,
            allowPipelineChange
          );

          if (response.mode === 'async') {
            onAsyncDispatched(response.job_id, response.total_count);
            onClose();
            return;
          }

          hasMore = response.has_more;
        }

        if (hitIterationCap) {
          // Partial success — report it so the user knows to retry. Don't fall
          // through to the success toast; matching records likely remain.
          toast.error(messages.incomplete);
        } else {
          toast.success(messages.successTitle, {
            description: messages.successDescription,
          });
        }
        onSyncComplete();
        onClose();
      } catch (err) {
        const errorType = (err as { response?: { error_type?: string } })
          ?.response?.error_type;
        toast.error(translateBulkChangeStageError(errorType));
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      orgId,
      entityTypeName,
      associationTypeName,
      selection,
      onAsyncDispatched,
      onSyncComplete,
      onClose,
    ]
  );

  return { submit, isSubmitting };
}
